var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import Shape from './Shape';
import { equalWithTolerance, PI2, getPointOnPolar } from '../utils/math';
import { sectorBBox } from '../utils/bbox';
import { isPointInSector, isPointInSectorStroke } from '../geometry/contain/sector';
var shapeKeys = [
    'cx',
    'cy',
    'radius',
    'radiusI',
    'start',
    'end',
    'round',
];
var Sector = /** @class */ (function (_super) {
    __extends(Sector, _super);
    function Sector() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'sector';
        _this.shapeKeys = shapeKeys;
        return _this;
    }
    Sector.prototype.getDefaultAttr = function () {
        return __assign(__assign({}, _super.prototype.getDefaultAttr.call(this)), { cx: 0, cy: 0, radius: 0, radiusI: 0, start: 0, end: 0, round: false, borderRadius: 0 });
    };
    Sector.prototype.getAnimationKeys = function () {
        return __spreadArrays(_super.prototype.getAnimationKeys.call(this), ['cx', 'cy', 'start', 'end', 'radius', 'radiusI']);
    };
    Sector.prototype.brush = function (ctx) {
        var _a = this.attr, cx = _a.cx, cy = _a.cy, radius = _a.radius, radiusI = _a.radiusI, start = _a.start, end = _a.end, round = _a.round, borderRadius = _a.borderRadius;
        if (equalWithTolerance(start, end) && !this.isClip) {
            return;
        }
        var delta = Math.abs(start - end);
        if (equalWithTolerance(delta, PI2) || delta > PI2) {
            ctx.arc(cx, cy, radius, PI2, 0, true);
            ctx.moveTo(cx + radiusI, cy);
            ctx.arc(cx, cy, radiusI, 0, PI2, false);
            return;
        }
        var anticlockwise = end < start;
        if (round || !borderRadius || borderRadius === 0) {
            var roundStart = void 0;
            var roundEnd = void 0;
            ctx.arc(cx, cy, radiusI, end, start, !anticlockwise);
            if (round) {
                var _b = getPointOnPolar(cx, cy, (radius + radiusI) / 2, start), x = _b.x, y = _b.y;
                roundStart = !anticlockwise ? start - Math.PI : start + Math.PI;
                roundEnd = start;
                ctx.arc(x, y, Math.abs(radius - radiusI) / 2, roundStart, roundEnd, anticlockwise);
            }
            ctx.arc(cx, cy, radius, start, end, anticlockwise);
            if (round) {
                var _c = getPointOnPolar(cx, cy, (radius + radiusI) / 2, end), x = _c.x, y = _c.y;
                roundStart = end;
                roundEnd = !anticlockwise ? end + Math.PI : end - Math.PI;
                ctx.arc(x, y, Math.abs(radius - radiusI) / 2, roundStart, roundEnd, anticlockwise);
            }
            ctx.closePath();
        }
        else {
            var r1 = void 0;
            var r2 = void 0;
            var r3 = void 0;
            var r4 = void 0;
            if (typeof borderRadius === 'number') {
                r1 = r2 = r3 = r4 = borderRadius;
            }
            else {
                r1 = borderRadius[0], r2 = borderRadius[1], r3 = borderRadius[2], r4 = borderRadius[3];
            }
            var cta = end - start;
            var l1 = cta * this.attr.radiusI;
            var l2 = this.attr.radius - this.attr.radiusI;
            var l3 = cta * this.attr.radius;
            var l4 = this.attr.radius - this.attr.radiusI;
            r1 = Math.min(l1 / Math.PI, l2 / Math.PI, r1);
            r2 = Math.min(l2 / Math.PI, l3 / Math.PI, r2);
            r3 = Math.min(l3 / Math.PI, l4 / Math.PI, r3);
            r4 = Math.min(l4 / Math.PI, l1 / Math.PI, r4);
            var angle1 = r1 ? Math.asin(r1 / (r1 + this.attr.radiusI)) : 0;
            var angle2 = r2 ? Math.asin(r2 / (this.attr.radius - r2)) : 0;
            var angle3 = r3 ? Math.asin(r3 / (this.attr.radius - r3)) : 0;
            var angle4 = r4 ? Math.asin(r4 / (r4 + this.attr.radiusI)) : 0;
            var p0 = {
                x: this.attr.radiusI * Math.cos(start) + this.attr.cx,
                y: this.attr.radiusI * Math.sin(start) + this.attr.cy,
            };
            var p0a = {
                x: (this.attr.radiusI + r1) * Math.cos(start) + this.attr.cx,
                y: (this.attr.radiusI + r1) * Math.sin(start) + this.attr.cy,
            };
            var p1 = {
                x: (r1 + this.attr.radiusI) * Math.cos(angle1) * Math.cos(start) + this.attr.cx,
                y: (r1 + this.attr.radiusI) * Math.cos(angle1) * Math.sin(start) + this.attr.cy,
            };
            var p2 = {
                x: (this.attr.radius - r2) * Math.cos(angle2) * Math.cos(start) + this.attr.cx,
                y: (this.attr.radius - r2) * Math.cos(angle2) * Math.sin(start) + this.attr.cy,
            };
            var p3 = {
                x: this.attr.radius * Math.cos(start) + this.attr.cx,
                y: this.attr.radius * Math.sin(start) + this.attr.cy,
            };
            var p3a = {
                x: this.attr.radius * Math.cos(start + angle2) + this.attr.cx,
                y: this.attr.radius * Math.sin(start + angle2) + this.attr.cy,
            };
            var p4 = {
                x: this.attr.radius * Math.cos(end) + this.attr.cx,
                y: this.attr.radius * Math.sin(end) + this.attr.cy,
            };
            var p4a = {
                x: (this.attr.radius - r3) * Math.cos(end) + this.attr.cx,
                y: (this.attr.radius - r3) * Math.sin(end) + this.attr.cy,
            };
            var p5 = {
                x: (this.attr.radiusI + r4) * Math.cos(angle4) * Math.cos(end) + this.attr.cx,
                y: (this.attr.radiusI + r4) * Math.cos(angle4) * Math.sin(end) + this.attr.cy,
            };
            var p6 = {
                x: this.attr.radiusI * Math.cos(end) + this.attr.cx,
                y: this.attr.radiusI * Math.sin(end) + this.attr.cy,
            };
            var p6a = {
                x: this.attr.radiusI * Math.cos(end - angle4) + this.attr.cx,
                y: this.attr.radiusI * Math.sin(end - angle4) + this.attr.cy,
            };
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.arcTo(p3.x, p3.y, p3a.x, p3a.y, r2);
            ctx.arc(this.attr.cx, this.attr.cy, this.attr.radius, 
            // start,
            start + angle2, end - angle3, anticlockwise);
            ctx.arcTo(p4.x, p4.y, p4a.x, p4a.y, r3);
            ctx.lineTo(p5.x, p5.y);
            ctx.arcTo(p6.x, p6.y, p6a.x, p6a.y, r4);
            ctx.arc(this.attr.cx, this.attr.cy, this.attr.radiusI, end - angle4, start + angle1, !anticlockwise);
            ctx.arcTo(p0.x, p0.y, p0a.x, p0a.y, r1);
        }
    };
    Sector.prototype.isPointInFill = function (x, y) {
        var _a = this.attr, cx = _a.cx, cy = _a.cy, radius = _a.radius, radiusI = _a.radiusI, start = _a.start, end = _a.end;
        // todo round
        return isPointInSector(cx, cy, radius, radiusI, start, end, x, y);
    };
    Sector.prototype.isPointInStroke = function (x, y, lineWidth) {
        var _a = this.attr, cx = _a.cx, cy = _a.cy, radius = _a.radius, radiusI = _a.radiusI, start = _a.start, end = _a.end;
        return isPointInSectorStroke(cx, cy, radius, radiusI, start, end, lineWidth, x, y);
    };
    Sector.prototype.computeBBox = function () {
        var _a = this.attr, cx = _a.cx, cy = _a.cy, radius = _a.radius, radiusI = _a.radiusI, ostart = _a.start, oend = _a.end, round = _a.round;
        var start = ostart;
        var end = oend;
        if (round) {
            var delta = Math.asin((radius - radiusI) / radius) * (end > start ? 1 : -1);
            start -= delta;
            end += delta;
        }
        return sectorBBox(cx, cy, radius, radiusI, start, end);
    };
    return Sector;
}(Shape));
export default Sector;
